import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  FormGroup,
  Button,
  Alert,
  Divider,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Notifications,
  Email,
  Sms,
  Save,
  Refresh,
  Info,
  Schedule,
  Security,
  Work,
  Payment,
  Report
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { emailService } from '../../services/emailService';

/**
 * NotificationPreferences Component
 * 
 * Comprehensive notification preferences management:
 * - Email notification settings
 * - SMS notification settings (future)
 * - Frequency preferences
 * - Category-based preferences
 * - Quiet hours settings
 */
const NotificationPreferences = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState({
    email: {
      enabled: true,
      categories: {
        escrow: true,
        milestone: true,
        payment: true,
        dispute: true,
        verification: true,
        inspection: true,
        reminder: true,
        digest: true,
        security: true,
        system: false
      },
      frequency: {
        immediate: true,
        daily_digest: false,
        weekly_digest: true,
        monthly_summary: false
      }
    },
    sms: {
      enabled: false,
      categories: {
        payment: true,
        dispute: true,
        security: true,
        urgent_only: true
      }
    },
    quietHours: {
      enabled: false,
      startTime: '22:00',
      endTime: '08:00',
      timezone: 'Africa/Harare'
    },
    language: 'en',
    unsubscribeToken: null
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadPreferences();
  }, []);

  /**
   * Load current notification preferences
   */
  const loadPreferences = async () => {
    try {
      setLoading(true);
      const userPreferences = await emailService.getEmailPreferences();
      
      if (userPreferences) {
        setPreferences(prev => ({
          ...prev,
          ...userPreferences
        }));
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
      setError('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Save notification preferences
   */
  const savePreferences = async () => {
    try {
      setSaving(true);
      setError('');
      setMessage('');

      await emailService.updateEmailPreferences(preferences);
      setMessage('Notification preferences saved successfully!');
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Failed to save preferences:', error);
      setError('Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle email category toggle
   */
  const handleEmailCategoryChange = (category) => {
    setPreferences(prev => ({
      ...prev,
      email: {
        ...prev.email,
        categories: {
          ...prev.email.categories,
          [category]: !prev.email.categories[category]
        }
      }
    }));
  };

  /**
   * Handle email frequency change
   */
  const handleEmailFrequencyChange = (frequency) => {
    setPreferences(prev => ({
      ...prev,
      email: {
        ...prev.email,
        frequency: {
          ...prev.email.frequency,
          [frequency]: !prev.email.frequency[frequency]
        }
      }
    }));
  };

  /**
   * Handle SMS category toggle
   */
  const handleSMSCategoryChange = (category) => {
    setPreferences(prev => ({
      ...prev,
      sms: {
        ...prev.sms,
        categories: {
          ...prev.sms.categories,
          [category]: !prev.sms.categories[category]
        }
      }
    }));
  };

  /**
   * Handle quiet hours toggle
   */
  const handleQuietHoursChange = (field, value) => {
    setPreferences(prev => ({
      ...prev,
      quietHours: {
        ...prev.quietHours,
        [field]: value
      }
    }));
  };

  /**
   * Get category description
   */
  const getCategoryDescription = (category) => {
    const descriptions = {
      escrow: 'Wallet creation, funding, and completion notifications',
      milestone: 'Milestone submissions, approvals, and progress updates',
      payment: 'Payment processing, receipts, and transaction confirmations',
      dispute: 'Dispute raised, resolution updates, and mediation requests',
      verification: 'Verification assignments and completion notifications',
      inspection: 'Quality inspection scheduling and results',
      reminder: 'Due date reminders and pending action notifications',
      digest: 'Weekly and monthly summary emails',
      security: 'Login alerts, password changes, and security notifications',
      system: 'System maintenance, updates, and announcements'
    };
    
    return descriptions[category] || '';
  };

  /**
   * Get category icon
   */
  const getCategoryIcon = (category) => {
    const icons = {
      escrow: <Work />,
      milestone: <Schedule />,
      payment: <Payment />,
      dispute: <Report />,
      verification: <Security />,
      inspection: <Security />,
      reminder: <Schedule />,
      digest: <Email />,
      security: <Security />,
      system: <Info />
    };
    
    return icons[category] || <Notifications />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Loading notification preferences...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Notifications />
          Notification Preferences
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Customize how and when you receive notifications from Eqaya
        </Typography>
      </Box>

      {message && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Email Notifications */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Email />
          Email Notifications
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={preferences.email.enabled}
              onChange={(e) => setPreferences(prev => ({
                ...prev,
                email: { ...prev.email, enabled: e.target.checked }
              }))}
            />
          }
          label="Enable email notifications"
          sx={{ mb: 2 }}
        />

        {preferences.email.enabled && (
          <>
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" gutterBottom>
              Notification Categories
            </Typography>
            
            <Grid container spacing={2}>
              {Object.entries(preferences.email.categories).map(([category, enabled]) => (
                <Grid item xs={12} sm={6} key={category}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ mt: 0.5 }}>
                          {getCategoryIcon(category)}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={enabled}
                                onChange={() => handleEmailCategoryChange(category)}
                                size="small"
                              />
                            }
                            label={
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' ')}
                              </Typography>
                            }
                            sx={{ mb: 1 }}
                          />
                          <Typography variant="caption" color="text.secondary" display="block">
                            {getCategoryDescription(category)}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" gutterBottom>
              Email Frequency
            </Typography>
            
            <FormGroup>
              {Object.entries(preferences.email.frequency).map(([frequency, enabled]) => (
                <FormControlLabel
                  key={frequency}
                  control={
                    <Switch
                      checked={enabled}
                      onChange={() => handleEmailFrequencyChange(frequency)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        {frequency.charAt(0).toUpperCase() + frequency.slice(1).replace('_', ' ')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {frequency === 'immediate' && 'Receive notifications as they happen'}
                        {frequency === 'daily_digest' && 'One email per day with all notifications'}
                        {frequency === 'weekly_digest' && 'Weekly summary of your activity'}
                        {frequency === 'monthly_summary' && 'Monthly overview and statistics'}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </FormGroup>
          </>
        )}
      </Paper>

      {/* SMS Notifications (Coming Soon) */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, opacity: 0.7 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Sms />
          SMS Notifications
          <Chip label="Coming Soon" size="small" color="primary" />
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={preferences.sms.enabled}
              disabled
            />
          }
          label="Enable SMS notifications"
          sx={{ mb: 2 }}
        />
        
        <Typography variant="body2" color="text.secondary">
          SMS notifications will be available in a future update for urgent alerts and payment confirmations.
        </Typography>
      </Paper>

      {/* Quiet Hours */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Schedule />
          Quiet Hours
          <Tooltip title="Notifications will be delayed during quiet hours except for urgent alerts">
            <IconButton size="small">
              <Info fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={preferences.quietHours.enabled}
              onChange={(e) => handleQuietHoursChange('enabled', e.target.checked)}
            />
          }
          label="Enable quiet hours"
          sx={{ mb: 2 }}
        />

        {preferences.quietHours.enabled && (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Start Time</InputLabel>
                <Select
                  value={preferences.quietHours.startTime}
                  onChange={(e) => handleQuietHoursChange('startTime', e.target.value)}
                  label="Start Time"
                >
                  {Array.from({ length: 24 }, (_, i) => {
                    const hour = i.toString().padStart(2, '0');
                    return (
                      <MenuItem key={`${hour}:00`} value={`${hour}:00`}>
                        {hour}:00
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>End Time</InputLabel>
                <Select
                  value={preferences.quietHours.endTime}
                  onChange={(e) => handleQuietHoursChange('endTime', e.target.value)}
                  label="End Time"
                >
                  {Array.from({ length: 24 }, (_, i) => {
                    const hour = i.toString().padStart(2, '0');
                    return (
                      <MenuItem key={`${hour}:00`} value={`${hour}:00`}>
                        {hour}:00
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* General Settings */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          General Settings
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Language</InputLabel>
              <Select
                value={preferences.language}
                onChange={(e) => setPreferences(prev => ({
                  ...prev,
                  language: e.target.value
                }))}
                label="Language"
              >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="sn" disabled>Shona (Coming Soon)</MenuItem>
                <MenuItem value="nd" disabled>Ndebele (Coming Soon)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={loadPreferences}
          startIcon={<Refresh />}
          disabled={loading}
        >
          Reset to Saved
        </Button>
        
        <Button
          variant="contained"
          onClick={savePreferences}
          startIcon={<Save />}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </Box>

      {/* Unsubscribe Information */}
      <Paper elevation={1} sx={{ p: 2, mt: 3, backgroundColor: '#f9f9f9' }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Note:</strong> You can always unsubscribe from specific email types using the links in our emails. 
          Security and payment notifications cannot be disabled for account safety.
        </Typography>
      </Paper>
    </Box>
  );
};

export default NotificationPreferences;